/**
 * DashboardCardSkeleton — content-aware loading placeholders for site dashboard cards.
 *
 * Each variant mirrors the real card's header + body layout so the loading state
 * shapes match what will appear once data arrives. Uses plain MUI Skeleton with the
 * shared theme treatment (wave animation, rounded radius, --skeleton-bg token) — no
 * per-variant colour overrides needed.
 *
 * Contains:
 * - DashboardCardSkeleton — variant-switched skeleton (default export)
 */
import { Box, Card, Skeleton, Stack } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';

// Matches RelayCard's loading state's Card sx — full size, flex column.
const relayCardSx = {
  overflow: 'hidden',
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
};

// Matches IntrusionSummaryCard / SensorSummaryCard Card sx — full size, flex column.
const fullCardSx = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

// Matches PowerReadingsCard's Card sx — flex column filling available height.
const powerCardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const buttonRadiusSx = { borderRadius: 'var(--button-radius)' };
const radiusSmSx = { borderRadius: 'var(--radius-sm)' };

// ── Relay variant ─────────────────────────────────────────────────────────────
// Mirrors RelayCard's loading branch: icon-squircle + relay title + load-type
// chip, action icons, load carousel tile, schedule rows, sparkline bars.
function RelaySkeleton() {
  return (
    <Card sx={relayCardSx}>
      {/* Header: icon + relay label + chip + action icons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.5 }}>
        <Skeleton
          variant="rounded"
          width={32}
          height={32}
          sx={{ borderRadius: 'var(--card-icon-radius)', flexShrink: 0 }}
        />
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width={32} sx={{ fontSize: '1rem' }} />
          <Skeleton variant="rounded" width={96} height={20} sx={buttonRadiusSx} />
        </Stack>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Skeleton variant="circular" width={24} height={24} />
          <Skeleton variant="circular" width={24} height={24} />
        </Stack>
      </Box>

      {/* Load carousel tile */}
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Skeleton variant="rounded" width={54} height={54} sx={{ borderRadius: 1 }} />
            <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="text" width="68%" />
              <Skeleton variant="text" width="44%" />
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* Schedule rows */}
      <Box sx={{ borderTop: 1, borderColor: 'divider', px: 1.5, py: 1.25 }}>
        <Stack spacing={1}>
          {[0, 1].map(i => (
            <Stack key={`relay-sk-sched-${i}`} direction="row" spacing={1} alignItems="center">
              <Stack direction="row" spacing={0.5}>
                {[0, 1, 2].map(d => (
                  <Skeleton
                    key={`relay-sk-sched-${i}-d${d}`}
                    variant="rounded"
                    width={22}
                    height={22}
                    sx={radiusSmSx}
                  />
                ))}
              </Stack>
              <Skeleton variant="text" width="36%" sx={{ ml: 'auto' }} />
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* Sparkline bar chart */}
      <Box
        sx={{
          mt: 'auto',
          px: 1.5,
          pb: 1.25,
          display: 'grid',
          gridTemplateColumns: 'repeat(8, 1fr)',
          gap: 0.5,
          alignItems: 'end',
        }}
      >
        {[18, 28, 22, 42, 34, 48, 26, 38].map((height, i) => (
          <Skeleton key={`relay-sk-spark-${i}`} variant="rounded" height={height} sx={radiusSmSx} />
        ))}
      </Box>
    </Card>
  );
}

// ── Power variant ─────────────────────────────────────────────────────────────
// Mirrors PowerReadingsCard: icon + title header, large value text, sparkline rect.
function PowerSkeleton() {
  return (
    <Card sx={powerCardSx}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        {/* Header: icon + title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width="52%" sx={{ fontSize: '0.875rem' }} />
        </Box>

        {/* Body (pushed to bottom like the real card's flex-end body) */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
          <Skeleton variant="text" width={100} sx={{ fontSize: '2rem' }} />
          <Skeleton variant="text" width={72} sx={{ fontSize: '0.75rem', mt: 0.25 }} />
          <Skeleton variant="text" width={88} sx={{ fontSize: '0.75rem', mt: 1.5 }} />
          <Skeleton variant="rounded" height={52} sx={{ borderRadius: 1, mt: 0.5 }} />
        </Box>
      </Box>
    </Card>
  );
}

// ── Intrusion variant ─────────────────────────────────────────────────────────
// Mirrors IntrusionSummaryCard's loading branch exactly: icon squircle + title/
// subtitle + two circular action buttons, then 3 sensor input rows.
function IntrusionSkeleton() {
  return (
    <Card sx={fullCardSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.5, flexShrink: 0 }}>
        <Skeleton
          variant="rounded"
          width={32}
          height={32}
          sx={{ borderRadius: 'var(--card-icon-radius)', flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width="52%" sx={{ fontSize: '0.875rem' }} />
          <Skeleton variant="text" width="34%" sx={{ fontSize: '0.75rem' }} />
        </Box>
        <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
          <Skeleton variant="circular" width={28} height={28} />
          <Skeleton variant="circular" width={28} height={28} />
        </Stack>
      </Box>

      {/* Input rows */}
      <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
        {[0, 1, 2].map(i => (
          <Box
            key={`intrusion-sk-row-${i}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.25,
              minHeight: 46,
              borderTop: i === 0 ? 0 : 1,
              borderColor: 'divider',
            }}
          >
            <Skeleton variant="text" width={18} sx={{ fontSize: '0.875rem' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="text" width={`${62 - i * 8}%`} sx={{ fontSize: '0.875rem' }} />
              {i === 1 ? <Skeleton variant="text" width="38%" sx={{ fontSize: '0.75rem' }} /> : null}
            </Box>
          </Box>
        ))}
      </Box>
    </Card>
  );
}

// ── Sensor variant ────────────────────────────────────────────────────────────
// Mirrors SensorSummaryCard: icon squircle + title + settings icon button, then
// one reading row and one compact configured-sensor row.
function SensorSkeleton() {
  return (
    <Card sx={fullCardSx}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.5, flexShrink: 0 }}>
        <Skeleton
          variant="rounded"
          width={32}
          height={32}
          sx={{ borderRadius: 'var(--card-icon-radius)', flexShrink: 0 }}
        />
        <Skeleton variant="text" width="44%" sx={{ fontSize: '0.875rem', flex: 1 }} />
        <Skeleton variant="circular" width={28} height={28} sx={{ flexShrink: 0 }} />
      </Box>

      {/* Sensor rows */}
      <Box sx={{ flex: 1, borderTop: 1, borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'minmax(0, 1fr) 6rem 6rem' },
            gap: 'var(--space-4)',
            px: 'var(--space-4)',
            py: 'var(--space-3)',
          }}
        >
          <Skeleton variant="text" width="58%" sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }} />
          <Stack spacing={0.25}>
            <Skeleton variant="text" width={36} sx={{ fontSize: '0.75rem' }} />
            <Skeleton variant="text" width={48} sx={{ fontSize: '0.875rem' }} />
          </Stack>
          <Stack spacing={0.25}>
            <Skeleton variant="text" width={52} sx={{ fontSize: '0.75rem' }} />
            <Skeleton variant="text" width={44} sx={{ fontSize: '0.875rem' }} />
          </Stack>
        </Box>
        <Box sx={{ borderTop: 1, borderColor: 'divider', px: 'var(--space-4)', py: 'var(--space-2)' }}>
          <Skeleton variant="text" width="34%" />
        </Box>
      </Box>
    </Card>
  );
}

// ── Rail variant ──────────────────────────────────────────────────────────────
// Mirrors SiteDashboardRail's structure: a rail section header + detail rows.
// Intentionally minimal — the rail's own SiteDashboardRailSkeleton (with
// StructureRailSkeleton / UnitRailSkeleton etc.) is already coherent; this
// variant is provided for completeness if callers want a single-column
// rail-section placeholder outside the full rail.
function RailSkeleton() {
  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, px: 2, py: 1.5 }}>
        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width="62%" sx={{ fontSize: '0.95rem' }} />
          <Skeleton variant="text" width="46%" sx={{ fontSize: '0.8rem' }} />
        </Stack>
        <Skeleton variant="rounded" width={56} height={24} sx={buttonRadiusSx} />
      </Box>
      <Box
        sx={{
          mx: 2,
          my: 0.5,
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-default)',
          overflow: 'hidden',
        }}
      >
        {[0, 1, 2].map(i => (
          <Box
            key={`rail-sk-row-${i}`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.25,
              minHeight: 44,
              borderTop: i === 0 ? 0 : 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton variant="text" width={72} sx={{ fontSize: '0.75rem' }} />
              <Skeleton variant="text" width={`${70 - i * 10}%`} sx={{ fontSize: '0.875rem' }} />
            </Box>
            {i === 2 && <Skeleton variant="circular" width={28} height={28} />}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// ── Summary variant ───────────────────────────────────────────────────────────
// Mirrors AlarmsSummaryCard / MaintenanceSummaryCard: a Card with a toolbar row
// (icon + title + filter chips + search field + action button) and a data-grid
// body (column header row + data rows). Used for the alarms and maintenance
// summary cards in the bottom DashboardSection.
function SummarySkeleton() {
  const columns = { xs: '1.2fr 0.75fr', md: '1.4fr 0.75fr 0.75fr 0.65fr' };

  return (
    <Card
      sx={{
        height: '100%',
        minHeight: 410,
        display: 'flex',
        flexDirection: 'column',
        p: 'var(--space-3)',
        overflow: 'hidden',
      }}
    >
      {/* Toolbar: icon + title + filter chips + search field + action button */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        useFlexGap
        sx={{ flexShrink: 0, mb: 1.5, flexWrap: 'wrap' }}
      >
        <Skeleton variant="circular" width={28} height={28} />
        <Skeleton variant="text" width={132} sx={{ fontSize: '1rem' }} />
        <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
          {[72, 88].map(w => (
            <Skeleton key={`sk-sum-filter-${w}`} variant="rounded" width={w} height={32} sx={buttonRadiusSx} />
          ))}
        </Stack>
        <Skeleton
          variant="rounded"
          height={36}
          sx={{ ...buttonRadiusSx, flex: '1 1 220px', maxWidth: 280, ml: 'auto' }}
        />
        <Skeleton variant="rounded" width={96} height={36} sx={buttonRadiusSx} />
      </Stack>

      {/* Grid body */}
      <Box
        sx={{
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* Column headers */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: columns,
            gap: 1,
            px: 'var(--space-2)',
            py: 'var(--space-1)',
            bgcolor: 'var(--surface-secondary)',
            borderBottom: '1px solid var(--border-default)',
          }}
        >
          {[0, 1, 2, 3].map(i => (
            <Skeleton
              key={`sk-sum-head-${i}`}
              variant="text"
              width={i === 0 ? '64%' : '52%'}
              sx={{ display: { xs: i > 1 ? 'none' : 'block', md: 'block' } }}
            />
          ))}
        </Box>

        {/* Data rows */}
        {[0, 1, 2, 3, 4].map(row => (
          <Box
            key={`sk-sum-row-${row}`}
            sx={{
              display: 'grid',
              gridTemplateColumns: columns,
              gap: 1,
              alignItems: 'center',
              px: 'var(--space-2)',
              py: 'var(--space-1)',
              minHeight: 48,
              borderBottom: row === 4 ? 0 : '1px solid var(--border-default)',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <Skeleton variant="rounded" width={68} height={24} sx={buttonRadiusSx} />
              <Skeleton variant="text" width={`${62 - row * 5}%`} />
            </Stack>
            <Skeleton variant="text" width="72%" />
            <Skeleton variant="text" width="66%" sx={{ display: { xs: 'none', md: 'block' } }} />
            <Skeleton
              variant="rounded"
              width={72}
              height={24}
              sx={{ ...buttonRadiusSx, display: { xs: 'none', md: 'block' } }}
            />
          </Box>
        ))}
      </Box>
    </Card>
  );
}

// ── Generic fallback ──────────────────────────────────────────────────────────
function GenericSkeleton() {
  return (
    <Card sx={fullCardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.5, flexShrink: 0 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Skeleton variant="text" width="46%" sx={{ fontSize: '1rem' }} />
      </Box>
      <Box sx={{ flex: 1, px: 1.5, pb: 1.5 }}>
        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 'var(--radius-md)' }} />
      </Box>
    </Card>
  );
}

const VARIANT_MAP = {
  relay: RelaySkeleton,
  power: PowerSkeleton,
  intrusion: IntrusionSkeleton,
  sensor: SensorSkeleton,
  rail: RailSkeleton,
  summary: SummarySkeleton,
};

/**
 * Content-aware skeleton card for the site dashboard.
 *
 * @param {object}                                            props
 * @param {'relay'|'power'|'intrusion'|'sensor'|'rail'|'summary'} [props.variant='relay']
 */
export default function DashboardCardSkeleton({ variant = 'relay' }) {
  const VariantComponent = VARIANT_MAP[variant] ?? GenericSkeleton;
  return <VariantComponent />;
}

DashboardCardSkeleton.propTypes = {
  variant: PropTypes.oneOf(['relay', 'power', 'intrusion', 'sensor', 'rail', 'summary']),
};
